import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Box,
  Typography,
  TextField,
  Button,
  Chip,
  Divider,
  Card,
  CardContent,
  CardHeader,
  IconButton,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from "@mui/material";
import { Close, Check, GetApp } from "@mui/icons-material";
import { useMutation } from "@apollo/client";
import { useTranslation } from "react-i18next";
import {
  SubmissionReviewSubmission,
  SubmissionReviewActivity,
  ReviewAllocationRecord,
} from "../../types";
import { DocumentViewer } from "./DocumentViewer";
import { GRADE_SUBMISSION, GRADE_REVIEW } from "../../Queries";

interface ContentProps {
  submission: SubmissionReviewSubmission;
  activity: SubmissionReviewActivity;
  onRefresh: () => void;
}

interface Props extends ContentProps {
  open: boolean;
  onClose: () => void;
}

/**
 * The submission drill-down body (content, reflection, grading, received
 * reviews) — rendered inside the ParticipantsList dialog and directly on the
 * results page's student level.
 */
export function SubmissionDetailContent({
  submission,
  activity,
  onRefresh,
}: ContentProps) {
  const [t] = useTranslation();
  const [grade, setGrade] = useState(
    submission.grade != null ? submission.grade.toString() : "",
  );
  const [feedback, setFeedback] = useState(submission.instructorFeedback || "");
  const [grading, setGrading] = useState(false);

  const [gradeSubmission] = useMutation(GRADE_SUBMISSION, {
    onCompleted: () => {
      setGrading(false);
      onRefresh();
    },
  });

  const [gradeReview] = useMutation(GRADE_REVIEW, {
    onCompleted: () => onRefresh(),
  });

  const [editingReviewGrade, setEditingReviewGrade] = useState<string | null>(
    null,
  );
  const [reviewGradeValue, setReviewGradeValue] = useState("");

  const handleSaveGrade = () => {
    setGrading(true);
    gradeSubmission({
      variables: {
        submissionId: submission.id,
        grade: parseFloat(grade),
        feedback,
      },
    });
  };

  const allocations = submission.allocations || [];

  // Per-criterion averages for this submission
  const criterionRows = (activity.rubricItems ?? [])
    .filter((item) => item.itemType === "CH")
    .map((item) => {
      const values: number[] = [];
      allocations.forEach((alloc) => {
        alloc.review?.answers?.forEach((ans) => {
          if (ans.itemId === item.id && ans.value != null) {
            values.push(ans.value);
          }
        });
      });
      return {
        id: item.id,
        label: item.label,
        avg: values.length
          ? values.reduce((a, b) => a + b, 0) / values.length
          : null,
        count: values.length,
      };
    });

  return (
    <>
      {/* Submission content */}
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardHeader
          title={String(t("Submission Content"))}
          titleTypographyProps={{ variant: "subtitle1" }}
        />
        <CardContent>
          {submission.submissionFileUrl && (
            <Box sx={{ mb: 2 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 1,
                }}
              >
                <Typography variant="subtitle2">
                  {t("Attached File")}
                </Typography>
                <IconButton
                  component="a"
                  href={submission.submissionFileUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  size="small"
                >
                  <GetApp fontSize="small" />
                </IconButton>
              </Box>
              <DocumentViewer fileUrl={submission.submissionFileUrl} />
            </Box>
          )}

          {submission.submissionText && (
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                {t("Submission Text")}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  whiteSpace: "pre-wrap",
                  p: 2,
                  bgcolor: "action.hover",
                  borderRadius: 1,
                  maxHeight: 300,
                  overflow: "auto",
                }}
              >
                {submission.submissionText}
              </Typography>
            </Box>
          )}

          {!submission.submissionFileUrl && !submission.submissionText && (
            <Typography color="text.secondary">
              {t("No submission content.")}
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* Close-the-loop: student reflection (respond via the feedback
            field below — the student sees it on their results page) */}
      {submission.studentReflection && (
        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardHeader
            title={String(t("Student Reflection"))}
            titleTypographyProps={{ variant: "subtitle1" }}
          />
          <CardContent>
            <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
              {submission.studentReflection}
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Per-criterion breakdown — the question-level detail belongs at
          this drill-down level (survey results pattern) */}
      {criterionRows.length > 0 && (
        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardHeader
            title={String(t("Scores by Criterion"))}
            titleTypographyProps={{ variant: "subtitle1" }}
          />
          <CardContent sx={{ p: 0, "&:last-child": { pb: 0 } }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t("Criterion")}</TableCell>
                  <TableCell>{t("Average")}</TableCell>
                  <TableCell>{t("Reviews")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {criterionRows.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>{row.label}</TableCell>
                    <TableCell>
                      {row.avg != null ? (
                        <Chip
                          label={row.avg.toFixed(1)}
                          size="small"
                          color="primary"
                        />
                      ) : (
                        "-"
                      )}
                    </TableCell>
                    <TableCell>{row.count}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Grading section */}
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardHeader
          title={String(t("Grading"))}
          titleTypographyProps={{ variant: "subtitle1" }}
        />
        <CardContent>
          <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
            <TextField
              label={String(t("Grade"))}
              type="number"
              size="small"
              value={grade}
              onChange={(e) => setGrade(e.target.value)}
              sx={{ width: 120 }}
              inputProps={{ step: 0.5 }}
            />
            <Button
              variant="contained"
              size="small"
              startIcon={<Check />}
              onClick={handleSaveGrade}
              disabled={!grade || grading}
            >
              {t("Save Grade")}
            </Button>
          </Box>
          <TextField
            label={String(t("Instructor Feedback"))}
            multiline
            rows={3}
            fullWidth
            size="small"
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            placeholder={String(t("Provide feedback on this submission..."))}
          />
        </CardContent>
      </Card>

      {/* Reviews received with rubric answers */}
      {allocations.length > 0 && (
        <Card variant="outlined">
          <CardHeader
            title={`${String(t("Peer Reviews Received"))} (${
              allocations.length
            })`}
            titleTypographyProps={{ variant: "subtitle1" }}
          />
          <CardContent sx={{ p: 0, "&:last-child": { pb: 0 } }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t("Reviewer")}</TableCell>
                  <TableCell>{t("Status")}</TableCell>
                  <TableCell>{t("Feedback")}</TableCell>
                  <TableCell>{t("Review Grade")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {allocations.map((alloc: ReviewAllocationRecord) => (
                  <TableRow key={alloc.id}>
                    <TableCell>{alloc.reviewerName}</TableCell>
                    <TableCell>
                      <Chip
                        label={
                          alloc.reviewSubmitted
                            ? String(t("Completed"))
                            : String(t("Pending"))
                        }
                        color={alloc.reviewSubmitted ? "success" : "default"}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      {alloc.review?.answers &&
                      alloc.review.answers.length > 0 ? (
                        <Box>
                          {alloc.review.answers.map((ans) => (
                            <Box key={ans.id} sx={{ mb: 0.5 }}>
                              {ans.value != null && (
                                <Chip
                                  label={ans.value}
                                  size="small"
                                  color="primary"
                                  variant="outlined"
                                  sx={{ mr: 0.5 }}
                                />
                              )}
                              {ans.text && (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  {ans.text}
                                </Typography>
                              )}
                            </Box>
                          ))}
                        </Box>
                      ) : alloc.reviewSubmitted ? (
                        <Typography variant="caption" color="text.secondary">
                          {t("No rubric answers")}
                        </Typography>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          -
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {alloc.reviewSubmitted && alloc.review ? (
                        editingReviewGrade === alloc.id ? (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                            }}
                          >
                            <TextField
                              size="small"
                              type="number"
                              value={reviewGradeValue}
                              onChange={(e) =>
                                setReviewGradeValue(e.target.value)
                              }
                              sx={{ width: 70 }}
                              inputProps={{ step: 0.5 }}
                            />
                            <IconButton
                              size="small"
                              onClick={() => {
                                gradeReview({
                                  variables: {
                                    reviewId: alloc.review!.id,
                                    grade: parseFloat(reviewGradeValue),
                                  },
                                });
                                setEditingReviewGrade(null);
                              }}
                              disabled={!reviewGradeValue}
                            >
                              <Check fontSize="small" />
                            </IconButton>
                          </Box>
                        ) : (
                          <Typography
                            variant="body2"
                            sx={{ cursor: "pointer" }}
                            onClick={() => {
                              setEditingReviewGrade(alloc.id);
                              setReviewGradeValue(
                                alloc.review?.grade != null
                                  ? alloc.review.grade.toString()
                                  : "",
                              );
                            }}
                          >
                            {alloc.review?.grade != null
                              ? alloc.review.grade
                              : String(t("Click to grade"))}
                          </Typography>
                        )
                      ) : (
                        "-"
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </>
  );
}

export function SubmissionDetail({
  open,
  onClose,
  submission,
  activity,
  onRefresh,
}: Props) {
  const [t] = useTranslation();
  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box>
          <Typography variant="h6">
            {activity.submissionType === "LIV" ||
            activity.submitterMode === "TM"
              ? submission.groupName ?? submission.authorName
              : submission.authorName}
          </Typography>
          <Chip
            label={
              submission.status === "SB"
                ? String(t("Submitted"))
                : String(t("Draft"))
            }
            color={submission.status === "SB" ? "success" : "default"}
            size="small"
          />
          {submission.submittedAt && (
            <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
              {new Date(submission.submittedAt).toLocaleString()}
            </Typography>
          )}
        </Box>
        <IconButton onClick={onClose}>
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <SubmissionDetailContent
          submission={submission}
          activity={activity}
          onRefresh={onRefresh}
        />
      </DialogContent>
    </Dialog>
  );
}
